import { useEffect, useState } from "react";
import {
  ExecutionRow,
  FeatureFlags,
  getFeatureFlags,
  getRuntimeActivationReadiness,
  listExecutions,
  ReadinessResult,
} from "@/api/methodologyConsole";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  ErrorState,
  HashLabel,
  LifecycleBadge,
  LoadingState,
  ReasonCodes,
  StatCard,
} from "./primitives";

export default function RuntimeTab() {
  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [rows, setRows] = useState<ExecutionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getFeatureFlags(), getRuntimeActivationReadiness(), listExecutions()])
      .then(([f, r, x]) => {
        setFlags(f);
        setReadiness(r);
        setRows(x);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!flags || !rows) return <LoadingState label="Loading runtime state…" />;

  const blockers: string[] = [];
  if (!flags.runtime_enabled) blockers.push("runtime_disabled");
  if (!flags.composition_enabled) blockers.push("composition_disabled");
  if (!flags.applicability_enabled) blockers.push("applicability_disabled");
  if (!readiness?.ready)
    blockers.push(...(((readiness?.reason_codes as string[]) ?? []) || []));
  blockers.push("blueprint_not_approved");

  return (
    <div className="space-y-6">
      <Alert variant="destructive">
        <AlertDescription>
          The Composition Runtime is globally disabled. No execution can be started from this
          console, and no client-side switch exists to bypass the server gate.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="runtime_enabled" value={flags.runtime_enabled ? "Yes" : "No"} />
        <StatCard label="composition_enabled" value={flags.composition_enabled ? "Yes" : "No"} />
        <StatCard
          label="applicability_enabled"
          value={flags.applicability_enabled ? "Yes" : "No"}
        />
        <StatCard
          label="Activation ready"
          value={readiness?.ready ? "Yes" : "No"}
          hint="Server-derived"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Execute Blueprint</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button disabled title="Runtime execution is disabled">
            Execute Blueprint
          </Button>
          <p className="text-sm text-muted-foreground">
            This action is disabled. It becomes available only when every condition below is
            satisfied on the server.
          </p>
          <ReasonCodes codes={blockers} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Execution history</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <EmptyState
              title="No runtime executions"
              description="The Composition Runtime has never executed in this environment. Executions appear here once composition is activated."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Modules</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead>Execution hash</TableHead>
                  <TableHead>Failure</TableHead>
                  <TableHead>Executed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.execution_id}>
                    <TableCell>
                      <LifecycleBadge status={r.status} />
                    </TableCell>
                    <TableCell>{r.module_count ?? 0}</TableCell>
                    <TableCell>{r.question_count ?? 0}</TableCell>
                    <TableCell>
                      <HashLabel value={r.execution_hash} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.failure_code ? `${r.failure_stage}: ${r.failure_code}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.executed_at
                        ? new Date(r.executed_at).toLocaleString("en-US")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            A successful execution permanently freezes its assessment composition. Frozen rows
            cannot be edited from any interface.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
